import React, { useState } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Typography,
  Box,
  IconButton,
  Chip
} from '@material-ui/core'
import {
  Close as CloseIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  Sms as SmsIcon,
  DirectionsCar as VisitIcon,
  Note as NoteIcon,
  Save as SaveIcon
} from '@material-ui/icons'
import { toast } from 'react-toastify'
import axios from 'axios'
import moment from 'moment'

const LeadActivityModal = ({ open, lead, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    type: 'phone_call',
    description: '',
    outcome: '',
    duration: '',
    contactPerson: lead?.contactName || '',
    nextAction: '',
    nextActionDate: ''
  })
  const [loading, setLoading] = useState(false)

  const activityTypes = [
    { value: 'phone_call', label: 'Phone Call', icon: <PhoneIcon /> },
    { value: 'email', label: 'Email', icon: <EmailIcon /> },
    { value: 'sms', label: 'SMS/Text', icon: <SmsIcon /> },
    { value: 'visit', label: 'Site Visit', icon: <VisitIcon /> },
    { value: 'note', label: 'Note', icon: <NoteIcon /> }
  ]

  const outcomeOptions = {
    phone_call: [
      'Spoke with decision maker',
      'Left voicemail',
      'No answer',
      'Wrong number',
      'Scheduled follow-up',
      'Not interested',
      'Requested information'
    ],
    email: [
      'Sent proposal',
      'Sent information',
      'Follow-up email',
      'Introduction email',
      'Thank you email'
    ],
    sms: [
      'Sent information',
      'Appointment reminder',
      'Follow-up message',
      'Introduction text'
    ],
    visit: [
      'Met with owner',
      'Left information',
      'Site assessment',
      'Nobody available',
      'Scheduled meeting'
    ],
    note: [
      'General note',
      'Internal update',
      'Research note',
      'Pricing note'
    ]
  }

  const handleChange = (field) => (event) => {
    setFormData({
      ...formData,
      [field]: event.target.value
    })
  }

  const handleSubmit = async () => {
    if (!formData.description) {
      toast.error('Please add a description')
      return
    }

    setLoading(true)
    try {
      const response = await axios.post(
        `/api/company/leads/${lead._id}/activities`,
        formData
      )

      if (response.data.success) {
        toast.success('Activity added successfully')
        onSave()
        handleClose()
      }
    } catch (error) {
      console.error('Error adding activity:', error)
      toast.error('Failed to add activity')
    } finally {
      setLoading(false)
    }
  }

  const handleClose = () => {
    setFormData({
      type: 'phone_call',
      description: '',
      outcome: '',
      duration: '',
      contactPerson: lead?.contactName || '',
      nextAction: '',
      nextActionDate: ''
    })
    onClose()
  }

  const getActivityIcon = (type) => {
    const activity = activityTypes.find(a => a.value === type)
    return activity?.icon || <NoteIcon />
  }

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box display="flex" alignItems="center" gap={1}>
            <Typography variant="h6">Add Activity</Typography>
            <Typography variant="body2" color="textSecondary">
              - {lead?.companyName}
            </Typography>
          </Box>
          <IconButton onClick={handleClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        <Grid container spacing={3}>
          {/* Activity Type */}
          <Grid item xs={12}>
            <Typography variant="subtitle2" gutterBottom>
              Activity Type
            </Typography>
            <Box display="flex" gap={1}>
              {activityTypes.map((activity) => (
                <Chip
                  key={activity.value}
                  label={activity.label}
                  icon={activity.icon}
                  onClick={() => setFormData({ ...formData, type: activity.value })}
                  color={formData.type === activity.value ? 'primary' : 'default'}
                  variant={formData.type === activity.value ? 'default' : 'outlined'}
                />
              ))}
            </Box>
          </Grid>

          {/* Contact Person */}
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Contact Person"
              value={formData.contactPerson}
              onChange={handleChange('contactPerson')}
              placeholder={lead?.contactName || 'Enter contact name'}
            />
          </Grid>

          {/* Duration (for phone calls) */}
          {formData.type === 'phone_call' && (
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Call Duration (minutes)"
                type="number"
                value={formData.duration}
                onChange={handleChange('duration')}
                placeholder="e.g., 15"
              />
            </Grid>
          )}

          {/* Outcome */}
          <Grid item xs={12} md={formData.type === 'phone_call' ? 12 : 6}>
            <FormControl fullWidth>
              <InputLabel>Outcome</InputLabel>
              <Select
                value={formData.outcome}
                onChange={handleChange('outcome')}
                label="Outcome"
              >
                <MenuItem value="">
                  <em>None</em>
                </MenuItem>
                {outcomeOptions[formData.type]?.map((outcome) => (
                  <MenuItem key={outcome} value={outcome}>
                    {outcome}
                  </MenuItem>
                ))}
                <MenuItem value="other">Other</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          {/* Description */}
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Description / Notes *"
              multiline
              rows={4}
              value={formData.description}
              onChange={handleChange('description')}
              placeholder={
                formData.type === 'phone_call' ? 'Summary of the call...' :
                formData.type === 'email' ? 'What was discussed in the email...' :
                formData.type === 'sms' ? 'Message content or response...' :
                formData.type === 'visit' ? 'Details of the visit...' :
                'Add your notes here...'
              }
            />
          </Grid>

          {/* Next Action */}
          <Grid item xs={12}>
            <Typography variant="subtitle2" gutterBottom>
              Follow-up (Optional)
            </Typography>
          </Grid>

          <Grid item xs={12} md={8}>
            <TextField
              fullWidth
              label="Next Action"
              value={formData.nextAction}
              onChange={handleChange('nextAction')}
              placeholder="e.g., Send proposal, Schedule meeting, Follow up call"
            />
          </Grid>

          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="Next Action Date"
              type="date"
              value={formData.nextActionDate}
              onChange={handleChange('nextActionDate')}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>

          {/* Quick Templates */}
          <Grid item xs={12}>
            <Typography variant="caption" color="textSecondary">
              Quick Templates:
            </Typography>
            <Box display="flex" gap={0.5} flexWrap="wrap" marginTop={1}>
              {formData.type === 'phone_call' && (
                <>
                  <Chip
                    label="Interested - Send info"
                    size="small"
                    onClick={() => setFormData({
                      ...formData,
                      description: 'Spoke with owner, interested in services. Will send detailed information.',
                      outcome: 'Spoke with decision maker',
                      nextAction: 'Send proposal'
                    })}
                  />
                  <Chip
                    label="Left VM"
                    size="small"
                    onClick={() => setFormData({
                      ...formData,
                      description: 'Left voicemail with callback number.',
                      outcome: 'Left voicemail',
                      nextAction: 'Follow up call'
                    })}
                  />
                </>
              )}
              {formData.type === 'email' && (
                <>
                  <Chip
                    label="Sent Proposal"
                    size="small"
                    onClick={() => setFormData({
                      ...formData,
                      description: 'Sent detailed proposal with pricing.',
                      outcome: 'Sent proposal',
                      nextAction: 'Follow up call'
                    })}
                  />
                </>
              )}
              {formData.type === 'visit' && (
                <>
                  <Chip
                    label="Site Assessment"
                    size="small"
                    onClick={() => setFormData({
                      ...formData,
                      description: 'Completed site assessment. Property needs...',
                      outcome: 'Site assessment',
                      nextAction: 'Prepare proposal'
                    })}
                  />
                </>
              )}
            </Box>
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          color="primary"
          startIcon={<SaveIcon />}
          disabled={loading || !formData.description}
        >
          {loading ? 'Saving...' : 'Save Activity'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default LeadActivityModal